import { useEffect, useRef, useState } from 'react';
import { Download, LoaderCircle, Minus, Plus } from 'lucide-react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { Modale } from '../ui/Modale';
import type { CorpsErreurApi } from '../../lib/types';

type EtatDocument =
  | { statut: 'chargement' }
  | { statut: 'erreur'; message: string }
  | { statut: 'pret'; pdf: PDFDocumentProxy };

const ZOOMS = [0.75, 1, 1.25, 1.5, 2, 3] as const;
/** Au-delà, une ligne de texte devient trop longue à lire sur grand écran. */
const LARGEUR_MAX_PAGE = 900;
/** Format A4 en attendant les dimensions de la page. */
const RATIO_A4 = 297 / 210;

/**
 * pdf.js n'est chargé qu'à l'ouverture d'un PDF : le lecteur n'en paie le
 * poids que lorsqu'il en a besoin. Version « legacy », lisible par les
 * tablettes qui ne sont plus mises à jour.
 */
async function chargerPdfJs() {
  const [pdfjs, travailleur] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = travailleur.default;
  return pdfjs;
}

/** Message d'une réponse en erreur de l'API, ou celui par défaut. */
async function messageDeLaReponse(reponse: Response): Promise<string> {
  try {
    const corps = (await reponse.json()) as CorpsErreurApi;
    if (corps.erreur?.message) return corps.erreur.message;
  } catch {
    // Corps non JSON : le message par défaut suffit.
  }
  return 'Le PDF n’a pas pu être ouvert.';
}

/**
 * Une page, dessinée quand elle approche de l'écran et effacée quand elle
 * s'en éloigne : un long support de cours ne garde pas en mémoire des
 * dizaines de pages dessinées, ce qu'un téléphone ne tient pas.
 */
function PagePdf({ pdf, numero, largeur }: { pdf: PDFDocumentProxy; numero: number; largeur: number }): JSX.Element {
  const cadre = useRef<HTMLDivElement>(null);
  const toile = useRef<HTMLCanvasElement>(null);
  const [proche, setProche] = useState(numero <= 2);
  const [ratio, setRatio] = useState(RATIO_A4);

  useEffect(() => {
    const element = cadre.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observateur = new IntersectionObserver(([entree]) => setProche(entree?.isIntersecting ?? false), {
      rootMargin: '150% 0px',
    });
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  useEffect(() => {
    const element = toile.current;
    if (!element) return;
    if (!proche || largeur <= 0) {
      element.width = 0;
      element.height = 0;
      return;
    }
    let tache: RenderTask | null = null;
    let annule = false;
    void pdf.getPage(numero).then((page) => {
      if (annule) return;
      const naturelle = page.getViewport({ scale: 1 });
      setRatio(naturelle.height / naturelle.width);
      const densite = Math.min(window.devicePixelRatio || 1, 2);
      const vue = page.getViewport({ scale: (largeur / naturelle.width) * densite });
      element.width = Math.floor(vue.width);
      element.height = Math.floor(vue.height);
      tache = page.render({ canvas: element, viewport: vue });
      // Une page annulée (défilement, zoom) rejette sa tâche : rien à signaler.
      tache.promise.catch(() => undefined);
    });
    return () => {
      annule = true;
      tache?.cancel();
    };
  }, [pdf, numero, largeur, proche]);

  return (
    <div
      ref={cadre}
      className="mx-auto bg-white shadow-sm"
      style={{ width: largeur, height: Math.round(largeur * ratio) }}
      data-page={numero}
    >
      <canvas ref={toile} className="block h-full w-full" role="img" aria-label={`Page ${numero}`} />
    </div>
  );
}

/**
 * Consultation d'un PDF dans le lecteur (TF-447), sans le télécharger : ses
 * pages sont dessinées par pdf.js dans la page, de la même façon sur
 * ordinateur, iPad et téléphone Android — le lecteur PDF intégré du
 * navigateur, lui, ne s'ouvre pas dans une page sur Chrome Android, qui
 * télécharge le fichier (décision du 24/10/2026).
 *
 * Le fichier passe par la route gardée du lecteur, qui vérifie les droits et
 * l'empreinte et trace la consultation ; le téléchargement reste à côté.
 */
export function VisionneusePdf({
  titre,
  nomFichier,
  source,
  telechargement,
  onFermer,
}: {
  titre: string;
  nomFichier: string;
  /** Adresse de consultation (`?inline=1`). */
  source: string;
  /** Adresse de téléchargement, sous le nom d'origine. */
  telechargement: string;
  onFermer: () => void;
}): JSX.Element {
  const [etat, setEtat] = useState<EtatDocument>({ statut: 'chargement' });
  const [zoom, setZoom] = useState(1);
  const [largeurDisponible, setLargeurDisponible] = useState(0);
  const zone = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let annule = false;
    let chargement: PDFDocumentLoadingTask | null = null;
    const controleur = new AbortController();
    setEtat({ statut: 'chargement' });
    void (async () => {
      try {
        const reponse = await fetch(source, { credentials: 'include', signal: controleur.signal });
        if (!reponse.ok) {
          const message = await messageDeLaReponse(reponse);
          if (!annule) setEtat({ statut: 'erreur', message });
          return;
        }
        const [donnees, pdfjs] = await Promise.all([reponse.arrayBuffer(), chargerPdfJs()]);
        if (annule) return;
        chargement = pdfjs.getDocument({ data: new Uint8Array(donnees), enableXfa: false });
        const pdf = await chargement.promise;
        if (!annule) setEtat({ statut: 'pret', pdf });
      } catch (erreur) {
        if (annule || (erreur instanceof DOMException && erreur.name === 'AbortError')) return;
        setEtat({ statut: 'erreur', message: 'Ce PDF ne s’affiche pas dans le lecteur : téléchargez-le pour le consulter.' });
      }
    })();
    return () => {
      annule = true;
      controleur.abort();
      // Libère le document et son fil de calcul, même à moitié chargé.
      void chargement?.destroy();
    };
  }, [source]);

  // La page suit la largeur de la fenêtre : rotation d'une tablette comprise.
  useEffect(() => {
    const element = zone.current;
    if (!element) return;
    const mesurer = () => setLargeurDisponible(element.clientWidth);
    mesurer();
    if (typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  const rang = ZOOMS.indexOf(zoom as (typeof ZOOMS)[number]);
  const largeurPage = Math.floor(Math.min(Math.max(largeurDisponible - 16, 0), LARGEUR_MAX_PAGE) * zoom);
  const pages = etat.statut === 'pret' ? etat.pdf.numPages : 0;

  return (
    <Modale titre={titre} sousTitre={nomFichier} ouverte onFermer={onFermer} largeur="max-w-5xl">
      <div className="space-y-3 px-3 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1" role="group" aria-label="Zoom">
            <button
              type="button"
              onClick={() => setZoom(ZOOMS[Math.max(rang - 1, 0)]!)}
              disabled={rang <= 0 || etat.statut !== 'pret'}
              aria-label="Réduire"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-codialis-navy hover:bg-gray-50 disabled:opacity-40 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
            >
              <Minus className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              disabled={etat.statut !== 'pret'}
              className="min-w-[56px] rounded-lg px-2 py-2 text-xs font-semibold text-codialis-navy hover:bg-gray-50 disabled:opacity-40"
              title="Ajuster à la largeur"
            >
              {Math.round(zoom * 100)} %
            </button>
            <button
              type="button"
              onClick={() => setZoom(ZOOMS[Math.min(rang + 1, ZOOMS.length - 1)]!)}
              disabled={rang >= ZOOMS.length - 1 || etat.statut !== 'pret'}
              aria-label="Agrandir"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-codialis-navy hover:bg-gray-50 disabled:opacity-40 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
            >
              <Plus className="h-4 w-4" aria-hidden />
            </button>
            {pages > 0 && (
              <span className="ml-2 text-xs text-codialis-gray">
                {pages} page{pages > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <a
            href={telechargement}
            download={nomFichier}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            Télécharger
          </a>
        </div>

        {/* Hauteur en unités de fenêtre : le document occupe la place
            disponible, la barre d'outils et la fermeture restent en vue. */}
        <div
          ref={zone}
          className="scrollbar-thin h-[70vh] overflow-auto rounded-xl bg-codialis-sand p-2 sm:h-[75vh]"
          tabIndex={0}
          aria-label={`Pages de ${nomFichier}`}
        >
          {etat.statut === 'chargement' && (
            <p className="flex h-full items-center justify-center gap-2 text-sm text-codialis-gray" role="status">
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />
              Ouverture du PDF…
            </p>
          )}
          {etat.statut === 'erreur' && (
            <div className="flex h-full items-center justify-center p-4 text-center">
              <p className="max-w-sm text-sm text-codialis-navy" role="alert">
                {etat.message}
              </p>
            </div>
          )}
          {etat.statut === 'pret' && largeurPage > 0 && (
            <div className="space-y-2" style={{ width: largeurPage > largeurDisponible - 16 ? largeurPage : undefined }}>
              {Array.from({ length: pages }, (_, index) => (
                <PagePdf key={index + 1} pdf={etat.pdf} numero={index + 1} largeur={largeurPage} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modale>
  );
}
